<script lang="ts">
  import { onDestroy, onMount, tick } from "svelte";
  import logoUrl from "../assets/sumatra-online-logo.svg";
  import {
    BROWSER_FEATURES_CHANGED_EVENT,
    checkBrowserFeatures,
    getCachedBrowserFeatures,
    type BrowserFeatures,
  } from "../browser-features";
  import {
    createFileState,
    createStoredDocumentFile,
    listHomeDocuments,
    loadFileStateFromOPFS,
    readHomeDocument,
    removeHomeDocument,
    renameHomeDocument,
    saveFileStateToOPFS,
    setHomeDocumentPinned,
    storedArchiveDirectoryExists,
    storedDocumentFromHomeDocument,
    type FileState,
    type HomeDocument,
    type StoredDocumentFile,
  } from "../opfs";
  import {
    compareURL,
    fileNameFromFileArg,
    isSimpleViewerPath,
    isValidNgFileName,
    ngOpenURL,
    readNgFile,
    simpleViewerURL,
    writeNgFile,
  } from "../ngstore";
  import { hasNgThumbnail, NG_THUMB_HEIGHT, NG_THUMB_WIDTH, writeNgThumbnail } from "../ngthumbs";
  import { ImageDocument, imageMimeTypeFromFileName, isBrowserImageFileName, isBrowserImageMimeType } from "../image-document";
  import { downloadServerFile, listServerFiles, removeServerFile, uploadServerFile, type ServerFile } from "../server-files";
  import type { User } from "../types";
  import { formatFileSize } from "../util";

  type LibraryDocument = HomeDocument & { onServer: boolean; local: boolean };

  type RenderTarget = "canvas" | "html";
  type DocumentKind = "pdf" | "djvu" | "comic" | "image" | "epub" | "mobi";
  type RenderableDocument = {
    readonly pageCount: number;
    readonly renderTarget?: RenderTarget;
    close(): void;
    renderPageFit(
      pageIndex: number,
      canvas: HTMLCanvasElement,
      maxWidth: number,
      maxHeight: number,
      host?: HTMLElement,
    ): { width: number; height: number } | Promise<{ width: number; height: number }>;
    renderThumbnail?(
      canvas: HTMLCanvasElement,
      maxWidth: number,
      maxHeight: number,
    ): { width: number; height: number } | Promise<{ width: number; height: number }>;
  };

  let {
    user = null,
    authLoading = false,
    onLogin = () => {},
    onLogout = () => {},
  } = $props<{
    user?: User | null;
    authLoading?: boolean;
    onLogin?: () => void;
    onLogout?: () => void;
  }>();

  let canvas: HTMLCanvasElement;
  let stage: HTMLDivElement;
  let htmlHost: HTMLDivElement;
  let fileInput: HTMLInputElement;
  let renameInput = $state<HTMLInputElement | null>(null);

  let currentDocument = $state<RenderableDocument | null>(null);
  let fileName = $state("");
  let pageIndex = $state(0);
  let pageCount = $state(0);
  let loading = $state(false);
  let loadingStatus = $state("Opening document...");
  let error = $state("");
  let isDragging = $state(false);
  let renderSize = $state<{ width: number; height: number } | null>(null);
  let browserFeatures = $state<BrowserFeatures | null>(null);
  let homeDocs = $state<LibraryDocument[]>([]);
  let homeFilter = $state("");
  let homeLoading = $state(true);
  let selectedHomeDocName = $state<string | null>(null);
  let renamingHomeDocName = $state<string | null>(null);
  let renameValue = $state("");
  let renameInputSize = $state(24);
  let homeMenuX = $state(0);
  let homeMenuY = $state(0);
  let homeMenuEl = $state<HTMLDivElement | null>(null);
  let homeActionBusy = $state(false);
  let storedFile: StoredDocumentFile | null = null;
  let fileState: FileState | null = null;
  let simpleViewerRoute = isSimpleViewerPath(window.location.pathname);
  let supportedFormatsText = $derived(supportedFormatsLabel(browserFeatures));
  let filteredHomeDocs = $derived.by(() => {
    const filter = homeFilter.trim().toLowerCase();
    if (!filter) {
      return homeDocs;
    }
    return homeDocs.filter((doc) => doc.originalFileName.toLowerCase().includes(filter));
  });

  let renderSeq = 0;
  let resizeObserver: ResizeObserver | null = null;

  onMount(() => {
    browserFeatures = getCachedBrowserFeatures();
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", onWindowClick);
    window.addEventListener("contextmenu", onWindowContextMenu);
    window.addEventListener(BROWSER_FEATURES_CHANGED_EVENT, onBrowserFeaturesChanged);
    window.addEventListener("pageshow", onPageShow);
    resizeObserver = new ResizeObserver(() => {
      renderCurrentPage();
    });
    resizeObserver.observe(stage);
    if (simpleViewerRoute) {
      homeLoading = false;
      void openSimpleViewerFromLocation();
    } else {
      loadHomeDocuments();
    }
    loadBrowserFeatures();
  });

  onDestroy(() => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("click", onWindowClick);
    window.removeEventListener("contextmenu", onWindowContextMenu);
    window.removeEventListener(BROWSER_FEATURES_CHANGED_EVENT, onBrowserFeaturesChanged);
    window.removeEventListener("pageshow", onPageShow);
    resizeObserver?.disconnect();
    revokeHomeDocumentURLs();
    currentDocument?.close();
  });

  // when going back from sumatrapdf-ng the browser can show this page as it was
  // when we left it and files could have been added or removed there
  function onPageShow(event: PageTransitionEvent) {
    if (!event.persisted || currentDocument) {
      return;
    }
    closeHomeActions();
    loading = false;
    loadingStatus = "Opening document...";
    void loadHomeDocuments();
  }

  function onBrowserFeaturesChanged(event: Event) {
    browserFeatures = event instanceof CustomEvent ? event.detail as BrowserFeatures : getCachedBrowserFeatures();
  }

  function loadBrowserFeatures() {
    void checkBrowserFeatures()
      .then((features) => {
        browserFeatures = features;
      })
      .catch((e) => {
        console.warn("failed to check browser image features", e);
      });
  }

  async function openFile(file: File) {
    const kind = documentKindFromFileName(file.name, file.type);
    if (!kind) {
      error = "Choose a PDF, DjVu, EPUB, MOBI, comic archive, or image file.";
      return;
    }

    loading = true;
    loadingStatus = kind === "comic" ? "Reading archive..." : "Opening document...";
    error = "";
    renderSize = null;

    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const prepared = kind === "comic" ? await prepareComicOpen(file, data) : await prepareDocumentOpen(data, file.name, kind);
      await saveDocument(file.name, data);
      currentDocument?.close();
      currentDocument = prepared.document;
      storedFile = prepared.stored;
      fileState = prepared.state;
      fileName = file.name;
      pageIndex = prepared.state.PageNo - 1;
      pageCount = prepared.document.pageCount;
      await tick();
      renderCurrentPage();
      void saveCurrentFileState();
      void createThumbnail(prepared.document, prepared.stored);
    } catch (e) {
      currentDocument?.close();
      currentDocument = null;
      storedFile = null;
      fileState = null;
      fileName = "";
      pageIndex = 0;
      pageCount = 0;
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
      loadingStatus = "Opening document...";
    }
  }

  async function openFiles(files: File[]) {
    if (files.length === 0) {
      return;
    }
    const [firstFile, ...backgroundFiles] = files;
    await openFile(firstFile);
    processFilesInBackground(backgroundFiles);
  }

  function processFilesInBackground(files: File[]) {
    if (files.length === 0) {
      return;
    }
    void (async () => {
      for (const file of files) {
        await processFileInBackground(file);
      }
    })();
  }

  async function processFileInBackground(file: File) {
    const kind = documentKindFromFileName(file.name, file.type);
    if (!kind) {
      return;
    }

    let prepared: { document: RenderableDocument; stored: StoredDocumentFile; state: FileState } | null = null;
    try {
      const data = new Uint8Array(await file.arrayBuffer());
      prepared = kind === "comic" ? await prepareComicOpen(file, data) : await prepareDocumentOpen(data, file.name, kind);
      await saveDocument(file.name, data);
      await saveFileStateToOPFS(prepared.stored, prepared.state);
      await createThumbnail(prepared.document, prepared.stored);
      await loadHomeDocuments();
    } catch (e) {
      console.warn("failed to process background document", file.name, e);
    } finally {
      prepared?.document.close();
    }
  }

  async function openSimpleViewerFromLocation() {
    const file = new URLSearchParams(window.location.search).get("file") ?? "";
    const name = fileNameFromFileArg(file);
    if (!name) {
      error = file ? "Invalid file." : "No file to open.";
      return;
    }
    await openNamedFile(name);
  }

  async function openNamedFile(name: string) {
    closeHomeActions();
    loading = true;
    loadingStatus = "Opening document...";
    error = "";
    renderSize = null;

    try {
      const kind = documentKindFromFileName(name);
      if (!kind) {
        throw new Error(`unsupported document type: ${name}`);
      }
      if (!(await ensureLocalCopy(name))) {
        return;
      }
      const prepared =
        kind === "comic" ? await prepareNamedComicOpen(name) : await prepareDocumentOpen(await readNgFile(name), name, kind);
      currentDocument?.close();
      currentDocument = prepared.document;
      storedFile = prepared.stored;
      fileState = prepared.state;
      fileName = name;
      pageIndex = prepared.state.PageNo - 1;
      pageCount = prepared.document.pageCount;
      await tick();
      renderCurrentPage();
      void saveCurrentFileState();
      void createThumbnail(prepared.document, prepared.stored);
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
      loadingStatus = "Opening document...";
    }
  }

  async function renderCurrentPage() {
    if (!currentDocument || !canvas || !stage) {
      return;
    }

    const seq = ++renderSeq;
    await tick();
    if (seq !== renderSeq) {
      return;
    }

    const padding = 0;
    const maxWidth = Math.max(240, stage.clientWidth - padding);
    const maxHeight = Math.max(240, stage.clientHeight - padding);

    try {
      if (currentDocument.renderTarget !== "html") {
        htmlHost?.replaceChildren();
      }
      renderSize = await currentDocument.renderPageFit(pageIndex, canvas, maxWidth, maxHeight, htmlHost);
      error = "";
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  function go(delta: number) {
    if (!currentDocument) {
      return;
    }
    const nextPage = Math.max(0, Math.min(pageCount - 1, pageIndex + delta));
    if (nextPage === pageIndex) {
      return;
    }
    pageIndex = nextPage;
    void saveCurrentFileState();
    renderCurrentPage();
  }

  function onKeyDown(event: KeyboardEvent) {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
      return;
    }

    if (event.key === "Escape" && currentDocument) {
      closeFile();
      event.preventDefault();
    } else if (event.key === "Escape" && selectedHomeDocName) {
      closeHomeActions();
      event.preventDefault();
    } else if (event.key === "ArrowRight" || event.key === "n") {
      go(1);
      event.preventDefault();
    } else if (event.key === "ArrowLeft" || event.key === "p") {
      go(-1);
      event.preventDefault();
    }
  }

  function onWindowClick() {
    if (!currentDocument) {
      closeHomeActions();
    }
  }

  function onWindowContextMenu(event: MouseEvent) {
    if (currentDocument) {
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest(".thumb-card, .home-action-popover")) {
      return;
    }
    closeHomeActions();
  }

  async function closeFile() {
    await saveCurrentFileState();
    if (simpleViewerRoute) {
      window.location.assign("/");
      return;
    }
    currentDocument?.close();
    currentDocument = null;
    storedFile = null;
    fileState = null;
    fileName = "";
    pageIndex = 0;
    pageCount = 0;
    renderSize = null;
    error = "";
    htmlHost?.replaceChildren();
    ++renderSeq;
    await loadHomeDocuments();
  }

  function onFileChange() {
    const files = Array.from(fileInput.files ?? []);
    if (files.length > 0) {
      void openPickedFiles(files);
    }
    fileInput.value = "";
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    isDragging = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) {
      void openPickedFiles(files);
    }
  }

  // the simple viewer opens files in place; the home page stores them and opens
  // the first in sumatrapdf-ng
  function openPickedFiles(files: File[]) {
    if (currentDocument || simpleViewerRoute) {
      return openFiles(files);
    }
    return storeAndOpenInSumatra(files);
  }

  async function storeAndOpenInSumatra(files: File[]) {
    const docs = files.filter((file) => documentKindFromFileName(file.name, file.type));
    if (docs.length === 0) {
      error = "Choose a PDF, DjVu, EPUB, MOBI, comic archive, or image file.";
      return;
    }
    closeHomeActions();
    loading = true;
    loadingStatus = "Saving...";
    error = "";
    try {
      for (const file of docs) {
        await writeNgFile(file.name, new Uint8Array(await file.arrayBuffer()));
      }
      window.location.assign(ngOpenURL(docs[0].name));
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      loading = false;
      loadingStatus = "Opening document...";
      await loadHomeDocuments();
    }
  }

  // Files not opened in sumatrapdf-ng or here since they were added (e.g. dropped
  // on the home page while another was opened) have no thumbnail. Make them one
  // at a time, once per page load.
  const thumbnailAttempted = new Set<string>();
  let fillingThumbnails = false;

  async function fillMissingThumbnails() {
    if (fillingThumbnails) {
      return;
    }
    fillingThumbnails = true;
    let made = false;
    try {
      for (;;) {
        const doc = homeDocs.find((d) => d.local && !d.thumbnailURL && !thumbnailAttempted.has(d.originalFileName));
        if (!doc || currentDocument || loading) {
          break;
        }
        thumbnailAttempted.add(doc.originalFileName);
        if (await makeThumbnail(doc)) {
          made = true;
        }
      }
    } finally {
      fillingThumbnails = false;
    }
    if (made && !currentDocument) {
      await loadHomeDocuments();
    }
  }

  async function makeThumbnail(doc: HomeDocument): Promise<boolean> {
    const name = doc.originalFileName;
    const kind = documentKindFromFileName(name);
    if (!kind) {
      return false;
    }
    let prepared: { document: RenderableDocument; stored: StoredDocumentFile; state: FileState } | null = null;
    try {
      prepared = kind === "comic" ? await prepareStoredComicOpen(doc) : await prepareDocumentOpen(await readNgFile(name), name, kind);
      const state: FileState = {
        ...prepared.state,
        OpenCount: doc.state.OpenCount,
        LastOpenedAt: new Date(doc.lastOpenedAtUnix * 1000).toISOString(),
        LastOpenedAtUnix: doc.lastOpenedAtUnix,
      };
      await saveFileStateToOPFS(prepared.stored, state);
      await createThumbnail(prepared.document, prepared.stored);
      return true;
    } catch (e) {
      console.warn("failed to make thumbnail", name, e);
      return false;
    } finally {
      prepared?.document.close();
    }
  }

  async function saveCurrentFileState() {
    if (!storedFile || !fileState) {
      return;
    }
    fileState = {
      ...fileState,
      PageNo: pageIndex + 1,
      PageCount: pageCount,
      Zoom: "fit page",
      ScrollPos: { X: stage?.scrollLeft ?? 0, Y: stage?.scrollTop ?? 0 },
    };
    await saveFileStateToOPFS(storedFile, fileState);
  }

  // the same thumbnail sumatrapdf-ng makes and shows (see ngthumbs.ts), made
  // if it has none or the document changed since
  async function createThumbnail(sourceDocument: RenderableDocument, sourceStoredFile: StoredDocumentFile) {
    const name = sourceStoredFile.originalFileName;
    try {
      if (await hasNgThumbnail(name)) {
        return;
      }
      // rendered larger and scaled down, which is sharper than a render at the final size
      const dx = NG_THUMB_WIDTH * 2;
      const dy = NG_THUMB_HEIGHT * 2;
      const thumbnailCanvas = document.createElement("canvas");
      if (sourceDocument.renderThumbnail) {
        await sourceDocument.renderThumbnail(thumbnailCanvas, dx, dy);
      } else if (sourceDocument.renderTarget === "html") {
        return;
      } else {
        await sourceDocument.renderPageFit(0, thumbnailCanvas, dx, dy);
      }
      await writeNgThumbnail(name, thumbnailCanvas);
    } catch (e) {
      console.warn("failed to create document thumbnail", e);
    }
  }

  function serverOnlyDocument(file: ServerFile): LibraryDocument {
    const stored = createStoredDocumentFilePlaceholder(file);
    const state = createFileState(stored, 0);
    state.OpenCount = 0;
    state.LastOpenedAtUnix = Math.floor(file.uploadedAt / 1000);
    return {
      state,
      hasState: false,
      sha1: "",
      originalFileName: file.name,
      size: file.size,
      thumbnailURL: null,
      lastOpenedAtUnix: state.LastOpenedAtUnix,
      onServer: true,
      local: false,
    };
  }

  function createStoredDocumentFilePlaceholder(file: ServerFile): StoredDocumentFile {
    const dot = file.name.lastIndexOf(".");
    const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "pdf";
    return {
      available: false,
      sha1: "",
      ext,
      fileName: "",
      stateFileName: "",
      originalFileName: file.name,
      size: file.size,
    };
  }

  async function loadHomeDocuments() {
    homeLoading = true;
    try {
      const localDocs = await listHomeDocuments();
      let serverFiles: ServerFile[] = [];
      if (user) {
        try {
          serverFiles = await listServerFiles();
        } catch (e) {
          console.warn("failed to load server files", e);
        }
      }
      const onServer = new Set(serverFiles.map((file) => file.name));
      const localNames = new Set(localDocs.map((doc) => doc.originalFileName));
      const docs: LibraryDocument[] = localDocs.map((doc) => ({
        ...doc,
        onServer: onServer.has(doc.originalFileName),
        local: true,
      }));
      for (const file of serverFiles) {
        if (!localNames.has(file.name)) {
          docs.push(serverOnlyDocument(file));
        }
      }
      docs.sort(
        (a, b) =>
          Number(b.state.IsPinned) - Number(a.state.IsPinned) ||
          b.lastOpenedAtUnix - a.lastOpenedAtUnix ||
          a.originalFileName.localeCompare(b.originalFileName),
      );
      revokeHomeDocumentURLs();
      homeDocs = docs;
    } catch (e) {
      console.warn("failed to load home documents", e);
      homeDocs = [];
    } finally {
      homeLoading = false;
    }
    if (!simpleViewerRoute) {
      void fillMissingThumbnails();
    }
  }

  async function ensureLocalCopy(name: string): Promise<boolean> {
    try {
      await readNgFile(name);
      return true;
    } catch {
      // not in this browser yet
    }
    if (!user) {
      error = `file not found: ${name}`;
      return false;
    }
    const wasLoading = loading;
    loading = true;
    loadingStatus = "Downloading...";
    error = "";
    try {
      const data = await downloadServerFile(name);
      await writeNgFile(name, data);
      return true;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      return false;
    } finally {
      if (!wasLoading) {
        loading = false;
      }
      loadingStatus = "Opening document...";
    }
  }

  function openHomeContextMenu(doc: HomeDocument, event: MouseEvent) {
    const toggleOff =
      event.type === "click" && selectedHomeDocName === doc.originalFileName && renamingHomeDocName !== doc.originalFileName;
    if (toggleOff) {
      closeHomeActions();
      return;
    }
    const x = event.clientX;
    const y = event.clientY;
    // The menu must open after this event. A right-click is contextmenu followed
    // by a click at the same point; showing the menu immediately puts Open under
    // that click.
    setTimeout(() => {
      if (currentDocument) {
        return;
      }
      selectedHomeDocName = doc.originalFileName;
      renamingHomeDocName = null;
      renameValue = doc.originalFileName;
      renameInputSize = inputSizeForFileName(doc.originalFileName);
      placeHomeMenu(x, y);
    }, 0);
  }

  function clampMenuPoint(x: number, y: number, width: number, height: number) {
    const pad = 8;
    return {
      x: Math.min(Math.max(pad, x), Math.max(pad, window.innerWidth - pad - width)),
      y: Math.min(Math.max(pad, y), Math.max(pad, window.innerHeight - pad - height)),
    };
  }

  function placeHomeMenu(x: number, y: number) {
    const first = clampMenuPoint(x, y, 220, 210);
    homeMenuX = first.x;
    homeMenuY = first.y;
    void tick().then(() => {
      const el = homeMenuEl;
      if (!el || renamingHomeDocName) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const next = clampMenuPoint(x, y, rect.width, rect.height);
      homeMenuX = next.x;
      homeMenuY = next.y;
    });
  }

  async function openHomeInSumatra(doc: LibraryDocument) {
    if (!(await ensureLocalCopy(doc.originalFileName))) {
      return;
    }
    window.location.assign(ngOpenURL(doc.originalFileName));
  }

  async function openHomeInSimpleViewer(doc: LibraryDocument) {
    if (!(await ensureLocalCopy(doc.originalFileName))) {
      return;
    }
    window.location.assign(simpleViewerURL(doc.originalFileName));
  }

  async function openHomeInCompare(doc: LibraryDocument) {
    if (!(await ensureLocalCopy(doc.originalFileName))) {
      return;
    }
    window.location.assign(compareURL(doc.originalFileName));
  }

  async function uploadHomeDocument(doc: LibraryDocument) {
    if (!doc.local) {
      return;
    }
    homeActionBusy = true;
    error = "";
    try {
      const data = await readNgFile(doc.originalFileName);
      await uploadServerFile(doc.originalFileName, data);
      closeHomeActions();
      await loadHomeDocuments();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      homeActionBusy = false;
    }
  }

  function closeHomeActions() {
    selectedHomeDocName = null;
    renamingHomeDocName = null;
    renameValue = "";
    renameInputSize = 24;
  }

  function cancelRenameHomeDocument(doc: HomeDocument) {
    renamingHomeDocName = null;
    renameValue = doc.originalFileName;
    renameInputSize = inputSizeForFileName(doc.originalFileName);
  }

  async function startRenameHomeDocument(doc: HomeDocument) {
    selectedHomeDocName = doc.originalFileName;
    renamingHomeDocName = doc.originalFileName;
    renameValue = doc.originalFileName;
    renameInputSize = inputSizeForFileName(doc.originalFileName);
    await tick();
    renameInput?.focus();
    renameInput?.select();
  }

  function inputSizeForFileName(fileName: string): number {
    return Math.max(24, fileName.length + 2);
  }

  async function renameSelectedHomeDocument(doc: HomeDocument) {
    const nextName = renameValue.trim();
    if (!nextName) {
      return;
    }
    if (!isValidNgFileName(nextName)) {
      error = "File name cannot contain / or \\.";
      return;
    }
    homeActionBusy = true;
    try {
      await renameHomeDocument(doc, nextName);
      closeHomeActions();
      await loadHomeDocuments();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      homeActionBusy = false;
    }
  }

  async function removeLocalHomeDocument(doc: HomeDocument) {
    homeActionBusy = true;
    try {
      await removeHomeDocument(doc);
      closeHomeActions();
      await loadHomeDocuments();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      homeActionBusy = false;
    }
  }

  async function removeServerHomeDocument(doc: HomeDocument) {
    homeActionBusy = true;
    error = "";
    try {
      await removeServerFile(doc.originalFileName);
      closeHomeActions();
      await loadHomeDocuments();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      homeActionBusy = false;
    }
  }

  async function togglePinnedHomeDocument(doc: HomeDocument) {
    homeActionBusy = true;
    try {
      await setHomeDocumentPinned(doc, !doc.state.IsPinned);
      closeHomeActions();
      await loadHomeDocuments();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      homeActionBusy = false;
    }
  }

  function revokeHomeDocumentURLs() {
    for (const doc of homeDocs) {
      if (doc.thumbnailURL) {
        URL.revokeObjectURL(doc.thumbnailURL);
      }
    }
  }

  // format libraries are most of the bundle and the home page doesn't need them until a file is opened
  async function openDocument(data: Uint8Array, kind: DocumentKind, fileName = ""): Promise<RenderableDocument> {
    if (kind === "djvu") {
      const { DjvuDocument } = await import("../djvu");
      return DjvuDocument.open(data);
    }
    if (kind === "image") {
      return ImageDocument.open(data);
    }
    if (kind === "epub") {
      const { EpubDocument } = await import("../epub-document");
      return EpubDocument.open(data);
    }
    if (kind === "mobi") {
      const { MobiDocument } = await import("../mobi-document");
      return MobiDocument.open(data, prefersKf8(fileName));
    }
    if (kind === "comic") {
      throw new Error("comic archive needs preprocessing before opening");
    }
    const { PdfDocument } = await import("../pdfium");
    return PdfDocument.open(data);
  }

  // saves the document where sumatrapdf-ng keeps its files
  async function saveDocument(name: string, data: Uint8Array) {
    try {
      await writeNgFile(name, data);
    } catch (e) {
      console.warn("failed to save document", name, e);
    }
  }

  async function prepareDocumentOpen(data: Uint8Array, originalFileName: string, kind: DocumentKind) {
    const nextDocument = kind === "image"
      ? await ImageDocument.open(data, imageMimeTypeFromFileName(originalFileName))
      : await openDocument(data, kind, originalFileName);
    const nextStoredFile = await createStoredDocumentFile(data, originalFileName);
    const previousState = await loadFileStateFromOPFS(nextStoredFile);
    return {
      document: nextDocument,
      stored: nextStoredFile,
      state: createFileState(nextStoredFile, nextDocument.pageCount, previousState),
    };
  }

  async function prepareComicOpen(file: File, data: Uint8Array) {
    const stored = await createStoredDocumentFile(data, file.name);
    const previousState = await loadFileStateFromOPFS(stored);
    const { prepareComicArchive } = await import("../comic-archive");
    return prepareComicArchive(file, data, previousState, (progress) => {
      loadingStatus = progress.total
        ? `Extracting file ${progress.current} / ${progress.total}`
        : `Extracting file ${progress.current}`;
    });
  }

  async function prepareNamedComicOpen(name: string) {
    const docs = await listHomeDocuments();
    const doc = docs.find((item) => item.originalFileName === name);
    if (!doc) {
      throw new Error(`file not found: ${name}`);
    }
    return prepareStoredComicOpen(doc);
  }

  async function prepareStoredComicOpen(doc: HomeDocument) {
    if (doc.hasState && doc.state.ArchiveFiles?.length) {
      const stored = storedDocumentFromHomeDocument(doc);
      // already extracted so no need to read the archive
      if (await storedArchiveDirectoryExists(stored)) {
        const { ComicArchiveDocument } = await import("../comic-archive");
        const nextDocument = new ComicArchiveDocument(stored, doc.state);
        return {
          document: nextDocument,
          stored,
          state: createFileState(stored, nextDocument.pageCount, doc.state),
        };
      }
    }
    const data = await readHomeDocument(doc);
    const file = new File([data as BlobPart], doc.originalFileName);
    return prepareComicOpen(file, data);
  }

  function documentKindFromFileName(fileName: string, mimeType = ""): DocumentKind | null {
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith(".pdf") || mimeType === "application/pdf") {
      return "pdf";
    }
    if (lowerName.endsWith(".djvu") || lowerName.endsWith(".djv") || mimeType === "image/vnd.djvu") {
      return "djvu";
    }
    if (lowerName.endsWith(".cbz") || lowerName.endsWith(".cbr") || lowerName.endsWith(".cb7")) {
      return "comic";
    }
    if (lowerName.endsWith(".epub") || mimeType === "application/epub+zip") {
      return "epub";
    }
    if (
      lowerName.endsWith(".mobi") ||
      lowerName.endsWith(".azw") ||
      lowerName.endsWith(".azw3") ||
      lowerName.endsWith(".prc") ||
      mimeType === "application/x-mobipocket-ebook" ||
      mimeType === "application/vnd.amazon.ebook"
    ) {
      return "mobi";
    }
    if (isBrowserImageMimeType(mimeType) || isBrowserImageFileName(fileName)) {
      return "image";
    }
    return null;
  }

  function formatLabel(doc: HomeDocument): string {
    const name = doc.originalFileName;
    const kind = documentKindFromFileName(name);
    if (kind === "pdf") {
      return "PDF";
    }
    if (kind === "epub") {
      return "EPUB";
    }
    if (kind === "djvu") {
      return "DJVU";
    }
    // sumatrapdf-ng supports formats that we don't
    return name.includes(".") ? name.split(".").pop()!.toUpperCase() : "FILE";
  }

  function prefersKf8(fileName: string): boolean {
    const lowerName = fileName.toLowerCase();
    return lowerName.endsWith(".azw3");
  }

  function supportedFormatsLabel(features: BrowserFeatures | null): string {
    const imageLabels = supportedImageFormatLabels(features);
    const imageText = features
      ? `images: ${imageLabels.length > 0 ? imageLabels.join(", ") : "none detected"}`
      : "images: detecting...";
    return [
      "PDF",
      "ePub",
      "MOBI",
      "cbr",
      "cbz",
      "cb7",
      "DjVu",
      imageText,
    ].join(", ");
  }

  function supportedImageFormatLabels(features: BrowserFeatures | null): string[] {
    if (!features) {
      return [];
    }
    const formats = features.imageFormats;
    const labels: Array<[string, string[]]> = [
      ["PNG", ["png"]],
      ["JPEG", ["jpg", "jpeg", "jfif"]],
      ["GIF", ["gif"]],
      ["BMP", ["bmp"]],
      ["WebP", ["webp"]],
      ["AVIF", ["avif"]],
      ["ICO", ["ico"]],
      ["SVG", ["svg"]],
    ];
    return labels
      .filter(([, keys]) => keys.some((key) => formats[key]))
      .map(([label]) => label);
  }
</script>

<section
  aria-label="Document reader"
  class:dragging={isDragging}
  class:reading={currentDocument !== null}
  class="viewer-shell"
  ondragenter={(event) => {
    event.preventDefault();
    isDragging = true;
  }}
  ondragover={(event) => event.preventDefault()}
  ondragleave={(event) => {
    if (event.currentTarget === event.target) {
      isDragging = false;
    }
  }}
  ondrop={onDrop}
>
  {#if !currentDocument && !simpleViewerRoute}
    <header class="home-toolbar">
      <a class="home-brand" href="/">
        <img src={logoUrl} width="48" height="48" alt="" />
        <span class="home-wordmark">Sumatra <span>Online</span></span>
      </a>
      <nav class="home-nav" aria-label="Main navigation">
        <a class="home-nav-btn selected" href="/" aria-current="page">Home</a>
        <a class="home-nav-btn" href="/sumatrapdf/">SumatraPDF</a>
        <a class="home-nav-btn" href="https://www.sumatrapdfreader.org/free-pdf-reader">Windows version</a>
      </nav>
      <div class="home-toolbar-actions">
        <span class="home-drop-hint">Drag &amp; drop or</span>
        <button class="home-open-file" onclick={() => fileInput.click()}>Open File</button>
        {#if user}
          <span class="home-user" title={user.email}>{user.email}</span>
          <button class="btn-outline" onclick={onLogout}>Logout</button>
        {:else if !authLoading}
          <button class="btn-outline-default" onclick={onLogin}>Login</button>
        {/if}
      </div>
    </header>
    <div class="home-filter-row">
      <input class="home-filter" type="search" placeholder="Filter by file name" bind:value={homeFilter} />
      <span class="supported-formats">{supportedFormatsText}</span>
    </div>
  {/if}

  <input
    bind:this={fileInput}
    class="hidden"
    type="file"
    multiple
    accept="application/pdf,application/epub+zip,application/x-mobipocket-ebook,application/vnd.amazon.ebook,image/vnd.djvu,image/png,image/jpeg,image/gif,image/bmp,image/webp,image/avif,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,.pdf,.epub,.mobi,.azw,.azw3,.prc,.djvu,.djv,.cbz,.cbr,.cb7,.png,.jpg,.jpeg,.jfif,.gif,.bmp,.webp,.avif,.ico,.svg"
    onchange={onFileChange}
  />

  {#if error}
    <div class="error">{error}</div>
  {/if}

  <div bind:this={stage} class="stage">
    {#if !currentDocument && simpleViewerRoute}
      <div class="home-content">
        <div class="home-status">
          {#if loading}
            {loadingStatus}
          {:else}
            <a href="/">Library</a>
          {/if}
        </div>
      </div>
    {:else if !currentDocument}
      <div class="home-content">
        {#if loading}
          <div class="home-status">{loadingStatus}</div>
        {:else if homeLoading}
          <div class="home-status">Loading documents...</div>
        {:else if filteredHomeDocs.length > 0}
          <div class="thumb-grid">
            {#each filteredHomeDocs as doc (doc.originalFileName)}
              <div class="thumb-item">
                <button
                  class="thumb-card"
                  class:selected={selectedHomeDocName === doc.originalFileName}
                  title={`${doc.originalFileName} (${formatFileSize(doc.size)})`}
                  onclick={(event) => {
                    event.stopPropagation();
                    if (event.detail > 1) {
                      return;
                    }
                    openHomeContextMenu(doc, event);
                  }}
                  ondblclick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    closeHomeActions();
                    openHomeInSumatra(doc);
                  }}
                  oncontextmenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openHomeContextMenu(doc, event);
                  }}
                >
                  <div class="thumb-image-wrap">
                    {#if doc.thumbnailURL}
                      <img class="thumb-image" src={doc.thumbnailURL} alt="" />
                    {:else}
                      <div class="thumb-placeholder">{formatLabel(doc)}</div>
                    {/if}
                    <div class="thumb-badges">
                      {#if doc.local}
                        <span class="thumb-badge thumb-local-badge">Local</span>
                      {/if}
                      {#if doc.onServer}
                        <span class="thumb-badge thumb-server-badge">Server</span>
                      {/if}
                    </div>
                  </div>
                  <div class="thumb-title">{doc.originalFileName}</div>
                </button>
                {#if selectedHomeDocName === doc.originalFileName}
                  <div
                    bind:this={homeMenuEl}
                    class="home-action-popover"
                    class:renaming={renamingHomeDocName === doc.originalFileName}
                    style={
                      renamingHomeDocName === doc.originalFileName ? undefined : `left: ${homeMenuX}px; top: ${homeMenuY}px`
                    }
                    oncontextmenu={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    {#if renamingHomeDocName === doc.originalFileName}
                      <form
                        class="home-rename-form"
                        onsubmit={(event) => {
                          event.preventDefault();
                          renameSelectedHomeDocument(doc);
                        }}
                      >
                        <input
                          bind:this={renameInput}
                          class="home-rename-input"
                          aria-label="Rename file"
                          size={renameInputSize}
                          bind:value={renameValue}
                          onclick={(event) => event.stopPropagation()}
                        />
                        <div class="home-action-row">
                          <button
                            class="home-action-button primary"
                            type="submit"
                            disabled={homeActionBusy || !renameValue.trim()}
                            onclick={(event) => event.stopPropagation()}
                          >
                            Save
                          </button>
                          <button
                            class="home-action-button"
                            type="button"
                            onclick={(event) => {
                              event.stopPropagation();
                              cancelRenameHomeDocument(doc);
                            }}
                            disabled={homeActionBusy}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    {:else}
                      <div class="home-action-menu">
                        {#if doc.local}
                          <a
                            class="home-action-button primary"
                            href={ngOpenURL(doc.originalFileName)}
                            onclick={(event) => event.stopPropagation()}
                          >
                            Open
                          </a>
                          <a
                            class="home-action-button"
                            href={simpleViewerURL(doc.originalFileName)}
                            onclick={(event) => event.stopPropagation()}
                          >
                            Open in simple viewer
                          </a>
                          {#if documentKindFromFileName(doc.originalFileName) === "pdf"}
                            <a
                              class="home-action-button"
                              href={compareURL(doc.originalFileName)}
                              onclick={(event) => event.stopPropagation()}
                            >
                              Compare PDF rendering engines
                            </a>
                          {/if}
                        {:else}
                          <button
                            class="home-action-button primary"
                            type="button"
                            onclick={(event) => {
                              event.stopPropagation();
                              void openHomeInSumatra(doc);
                            }}
                            disabled={homeActionBusy}
                          >
                            Open
                          </button>
                          <button
                            class="home-action-button"
                            type="button"
                            onclick={(event) => {
                              event.stopPropagation();
                              void openHomeInSimpleViewer(doc);
                            }}
                            disabled={homeActionBusy}
                          >
                            Open in simple viewer
                          </button>
                          {#if documentKindFromFileName(doc.originalFileName) === "pdf"}
                            <button
                              class="home-action-button"
                              type="button"
                              onclick={(event) => {
                                event.stopPropagation();
                                void openHomeInCompare(doc);
                              }}
                              disabled={homeActionBusy}
                            >
                              Compare PDF rendering engines
                            </button>
                          {/if}
                        {/if}
                        {#if user && doc.local && !doc.onServer}
                          <button
                            class="home-action-button"
                            type="button"
                            onclick={(event) => {
                              event.stopPropagation();
                              void uploadHomeDocument(doc);
                            }}
                            disabled={homeActionBusy}
                          >
                            Upload
                          </button>
                        {/if}
                        {#if doc.local}
                        <button
                          class="home-action-button"
                          type="button"
                          onclick={(event) => {
                            event.stopPropagation();
                            togglePinnedHomeDocument(doc);
                          }}
                          disabled={homeActionBusy}
                        >
                          {doc.state.IsPinned ? "Unpin" : "Pin"}
                        </button>
                        <button
                          class="home-action-button"
                          type="button"
                          onclick={(event) => {
                            event.stopPropagation();
                            startRenameHomeDocument(doc);
                          }}
                          disabled={homeActionBusy}
                        >
                          Rename
                        </button>
                        <button
                          class="home-action-button danger"
                          type="button"
                          onclick={(event) => {
                            event.stopPropagation();
                            removeLocalHomeDocument(doc);
                          }}
                          disabled={homeActionBusy}
                        >
                          Remove local
                        </button>
                        {/if}
                        {#if user && doc.onServer}
                          <button
                            class="home-action-button danger"
                            type="button"
                            onclick={(event) => {
                              event.stopPropagation();
                              void removeServerHomeDocument(doc);
                            }}
                            disabled={homeActionBusy}
                          >
                            Remove server
                          </button>
                        {/if}
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {:else if homeDocs.length > 0}
          <div class="home-status">No files match “{homeFilter}”.</div>
        {:else}
          <button class="drop-target" onclick={() => fileInput.click()}>
            <span class="text-base font-medium">Drop a PDF, DjVu, EPUB, MOBI, comic archive, or image here</span>
            <span class="text-gray-500 dark:text-gray-400">Opened documents will appear here as thumbnails.</span>
          </button>
        {/if}
      </div>
    {/if}
    <canvas
      bind:this={canvas}
      class:visible={currentDocument !== null && currentDocument.renderTarget !== "html"}
      class="pdf-canvas"
      aria-label="Document page canvas"
    ></canvas>
    <div
      bind:this={htmlHost}
      class:visible={currentDocument?.renderTarget === "html"}
      class="epub-host"
      aria-label="EPUB page"
    ></div>
  </div>

  {#if currentDocument}
    <div class="reader-overlay" aria-live="polite">
      <div class="reader-file-name" title={fileName}>{fileName}</div>
      <div class="reader-actions">
        <button class="overlay-page-button" title="Previous page" onclick={() => go(-1)} disabled={pageIndex === 0}>←</button>
        <span>{pageIndex + 1} / {pageCount}</span>
        <button class="overlay-page-button" title="Next page" onclick={() => go(1)} disabled={pageIndex >= pageCount - 1}>→</button>
        <span class="overlay-separator">|</span>
        <button class="overlay-close" title="Close document" aria-label="Close document" onclick={closeFile}>×</button>
      </div>
    </div>
  {/if}
</section>
